import { useState, useEffect, useRef } from 'react'
import { useVisualAutoSave } from '@/hooks/useVisualAutoSave'
import { useInterviewTimer } from '@/hooks/useInterviewTimer'
import { useInterviewStore } from '@/store/interviewStore'
import QuestionPanel from './QuestionPanel'
import TimerDisplay from './TimerDisplay'
import { 
  Layers, Database, Server, Cloud, Users, ArrowRight, 
  Globe, Shield, Zap, HardDrive, Cpu, Network, 
  MessageSquare, Bell, Search, BarChart3,
  Plus, Minus, Square, Circle, Triangle,
  Move, RotateCcw, Trash2, Download, Link, MousePointer
} from 'lucide-react'

const SYSTEM_COMPONENTS = [
  { id: 'load-balancer', name: 'Load Balancer', icon: Globe, color: 'bg-blue-500' },
  { id: 'web-server', name: 'Web Server', icon: Server, color: 'bg-green-500' },
  { id: 'app-server', name: 'App Server', icon: Cpu, color: 'bg-purple-500' },
  { id: 'database', name: 'Database', icon: Database, color: 'bg-yellow-500' },
  { id: 'cache', name: 'Cache', icon: Zap, color: 'bg-red-500' },
  { id: 'cdn', name: 'CDN', icon: Cloud, color: 'bg-indigo-500' },
  { id: 'queue', name: 'Message Queue', icon: MessageSquare, color: 'bg-pink-500' },
  { id: 'storage', name: 'File Storage', icon: HardDrive, color: 'bg-gray-500' },
  { id: 'search', name: 'Search Service', icon: Search, color: 'bg-orange-500' },
  { id: 'analytics', name: 'Analytics', icon: BarChart3, color: 'bg-teal-500' },
  { id: 'notification', name: 'Notifications', icon: Bell, color: 'bg-amber-500' },
  { id: 'security', name: 'Security/Auth', icon: Shield, color: 'bg-emerald-500' }
]

const DESIGN_TOOLS = [
  { id: 'move', name: 'Move', icon: MousePointer },
  { id: 'connect', name: 'Connect', icon: Link },
  { id: 'delete', name: 'Delete', icon: Trash2 }
]

const DEFAULT_SYSTEM_DESIGN_PROMPT = {
  title: 'Design a social media platform like Twitter',
  description:
    'The system should support millions of users posting short messages (tweets), following other users, and viewing a personalized timeline. Consider scalability, availability, and consistency requirements.',
  requirements: [
    '100M daily active users',
    'Users can post tweets (280 chars)',
    'Users can follow other users',
    'Real-time timeline updates',
    'Search functionality'
  ],
  considerations: [
    'Database partitioning strategy',
    'Caching mechanisms',
    'Message queues',
    'CDN for media content',
    'Rate limiting',
    'Data consistency'
  ]
}

const sanitizeBulletLine = (line: string): string => line.replace(/^[\-•\*]\s*/, '').trim()

const coerceStringList = (value?: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item.trim()
        if (item === null || item === undefined) return ''
        return String(item).trim()
      })
      .filter(Boolean)
  }
  return []
}

const extractNotesBreakdown = (notes?: string | null) => {
  if (!notes) return { description: '', requirements: [] as string[] }

  const lines = notes
    .split(/\r?\n/)
    .map((line) => sanitizeBulletLine(line))
    .filter(Boolean)

  if (!lines.length) {
    return { description: notes.trim(), requirements: [] as string[] }
  }

  const [firstLine, ...rest] = lines
  return {
    description: firstLine,
    requirements: rest
  }
}

export default function SystemDesign() {
  const store = useInterviewStore()
  const systemDesignData = store.getSystemDesignData()
  const promptDetails = store.systemDesignPrompt

  const parsedNotes = promptDetails?.parsedNotes || null
  const rawPromptNotes = typeof promptDetails?.notes === 'string' ? promptDetails.notes.trim() : ''

  let promptTitle = parsedNotes?.title?.trim() || DEFAULT_SYSTEM_DESIGN_PROMPT.title
  let promptDescription = parsedNotes?.description?.trim() || ''
  let promptRequirements = coerceStringList(parsedNotes?.requirements)
  let promptConsiderations = coerceStringList(parsedNotes?.considerations)
  const promptAdditionalNotes =
    typeof parsedNotes?.additionalNotes === 'string' ? parsedNotes.additionalNotes.trim() : ''

  if (!promptDescription) {
    const breakdown = extractNotesBreakdown(rawPromptNotes)
    if (breakdown.description) {
      promptDescription = breakdown.description
    }
    if (!promptRequirements.length && breakdown.requirements.length) {
      promptRequirements = breakdown.requirements
    }
  }

  if (!promptDescription) {
    promptDescription = rawPromptNotes || DEFAULT_SYSTEM_DESIGN_PROMPT.description
  }

  if (!promptRequirements.length) {
    promptRequirements = DEFAULT_SYSTEM_DESIGN_PROMPT.requirements
  }

  if (!promptConsiderations.length) {
    promptConsiderations = DEFAULT_SYSTEM_DESIGN_PROMPT.considerations
  }

  const promptRequirementsPreview = promptRequirements.slice(0, 3)
  const promptDescriptionPreview = promptDescription.length > 220
    ? `${promptDescription.slice(0, 217)}…`
    : promptDescription
  const interviewerDetails = promptDetails?.interviewer

  console.log('🔧 SystemDesign component loaded with tools:', systemDesignData.selectedTool)
  const { isDirty, lastSaved } = useVisualAutoSave({ 
    stage: 'system-design',
    intervalMs: 5000 
  })

  // Timer functionality
  const { 
    formattedTime, 
    timeColor, 
    timeStatus, 
    isRunning, 
    isTimeUp,
    startTimer, 
    pauseTimer, 
    resetTimer 
  } = useInterviewTimer({ 
    initialTimeMinutes: 60,
    onTimeUp: () => {
      alert('Time is up for the System Design challenge!')
    }
  })

  // Question state
  const [isQuestionCollapsed, setIsQuestionCollapsed] = useState(false)

  const canvasRef = useRef<HTMLDivElement>(null)
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null)

  const handleContentChange = (newContent: string) => {
    console.log('📝 System design content changed:', newContent.substring(0, 50) + '...')
    store.updateSystemDesignContent(newContent)
  }

  const handleComponentDrop = (componentId: string, x: number, y: number) => {
    const component = SYSTEM_COMPONENTS.find(c => c.id === componentId)
    if (component) {
      const newComponent = {
        id: `${componentId}-${Date.now()}`,
        componentId: component.id,
        name: component.name,
        icon: component.icon.name, // Store icon name instead of component
        color: component.color,
        x,
        y,
        width: 120,
        height: 80
      }
      store.addSystemDesignComponent(newComponent)
    }
  }

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const componentId = e.dataTransfer.getData('componentId')
    if (componentId && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      handleComponentDrop(componentId, x - 60, y - 40) // Center the component
    }
  }

  const handleComponentClick = (componentId: string) => {
    if (systemDesignData.selectedTool === 'connect') {
      if (!connectingFrom) {
        setConnectingFrom(componentId)
      } else if (connectingFrom !== componentId) {
        // Create connection
        const connection = {
          id: `conn-${Date.now()}`,
          type: 'arrow' as const,
          fromComponentId: connectingFrom,
          toComponentId: componentId,
          color: '#374151',
          strokeWidth: 2
        }
        store.addSystemDesignConnection(connection)
        setConnectingFrom(null)
      }
    } else if (systemDesignData.selectedTool === 'delete') {
      store.removeSystemDesignComponent(componentId)
    }
  }

  const clearCanvas = () => {
    store.updateSystemDesignComponents([])
    store.updateSystemDesignConnections([])
    setConnectingFrom(null)
  }

  const renderConnection = (connection: any) => {
    const fromComponent = systemDesignData.placedComponents.find(c => c.id === connection.fromComponentId)
    const toComponent = systemDesignData.placedComponents.find(c => c.id === connection.toComponentId)
    
    if (!fromComponent || !toComponent) return null

    const fromX = fromComponent.x + fromComponent.width / 2
    const fromY = fromComponent.y + fromComponent.height / 2
    const toX = toComponent.x + toComponent.width / 2
    const toY = toComponent.y + toComponent.height / 2

    // Create L-shaped path: horizontal first, then vertical
    const midX = fromX + (toX - fromX) * 0.7 // 70% of the way horizontally
    
    const arrowLength = 8
    const arrowWidth = 4

    // Calculate positions for directional flow arrows
    const flowArrows = []
    const spacing = 60 // Distance between flow arrows
    
    // Horizontal segment 1
    const horizontalDist1 = Math.abs(midX - fromX)
    if (horizontalDist1 > spacing) {
      const numArrows1 = Math.floor(horizontalDist1 / spacing)
      for (let i = 1; i <= numArrows1; i++) {
        const x = fromX + (midX - fromX) * (i / (numArrows1 + 1))
        flowArrows.push({ x, y: fromY, direction: 'horizontal', segment: 1 })
      }
    }
    
    // Vertical segment
    const verticalDist = Math.abs(toY - fromY)
    if (verticalDist > spacing) {
      const numArrows2 = Math.floor(verticalDist / spacing)
      for (let i = 1; i <= numArrows2; i++) {
        const y = fromY + (toY - fromY) * (i / (numArrows2 + 1))
        flowArrows.push({ x: midX, y, direction: 'vertical', segment: 2 })
      }
    }
    
    // Horizontal segment 2
    const horizontalDist2 = Math.abs(toX - midX)
    if (horizontalDist2 > spacing) {
      const numArrows3 = Math.floor(horizontalDist2 / spacing)
      for (let i = 1; i <= numArrows3; i++) {
        const x = midX + (toX - midX) * (i / (numArrows3 + 1))
        flowArrows.push({ x, y: toY, direction: 'horizontal', segment: 3 })
      }
    }

    return (
      <g key={connection.id} onClick={() => systemDesignData.selectedTool === 'delete' && store.removeSystemDesignConnection(connection.id)}>
        {/* Horizontal line */}
        <line
          x1={fromX}
          y1={fromY}
          x2={midX}
          y2={fromY}
          stroke={connection.color}
          strokeWidth={systemDesignData.selectedTool === 'delete' ? connection.strokeWidth + 1 : connection.strokeWidth}
          className={systemDesignData.selectedTool === 'delete' ? 'cursor-pointer hover:stroke-red-500 hover:stroke-4 transition-all' : ''}
          style={{ strokeDasharray: systemDesignData.selectedTool === 'delete' ? '4,2' : 'none' }}
        />
        {/* Vertical line */}
        <line
          x1={midX}
          y1={fromY}
          x2={midX}
          y2={toY}
          stroke={connection.color}
          strokeWidth={systemDesignData.selectedTool === 'delete' ? connection.strokeWidth + 1 : connection.strokeWidth}
          className={systemDesignData.selectedTool === 'delete' ? 'cursor-pointer hover:stroke-red-500 hover:stroke-4 transition-all' : ''}
          style={{ strokeDasharray: systemDesignData.selectedTool === 'delete' ? '4,2' : 'none' }}
        />
        {/* Final horizontal line to target */}
        <line
          x1={midX}
          y1={toY}
          x2={toX}
          y2={toY}
          stroke={connection.color}
          strokeWidth={systemDesignData.selectedTool === 'delete' ? connection.strokeWidth + 1 : connection.strokeWidth}
          className={systemDesignData.selectedTool === 'delete' ? 'cursor-pointer hover:stroke-red-500 hover:stroke-4 transition-all' : ''}
          style={{ strokeDasharray: systemDesignData.selectedTool === 'delete' ? '4,2' : 'none' }}
        />
        
        {/* Static directional flow arrows */}
        {flowArrows.map((arrow, index) => {
          const size = 7 // Increased from 5 to 7 for thicker arrows
          const isDeleteMode = systemDesignData.selectedTool === 'delete'
          
          if (arrow.direction === 'horizontal') {
            const direction = arrow.segment === 1 ? (midX > fromX ? 1 : -1) : (toX > midX ? 1 : -1)
            return (
              <polygon
                key={`flow-${index}`}
                points={`${arrow.x + size * direction},${arrow.y} ${arrow.x - size * direction},${arrow.y - size/2} ${arrow.x - size * direction},${arrow.y + size/2}`}
                fill={connection.color}
                opacity={isDeleteMode ? "1" : "0.7"}
                className={isDeleteMode ? 'cursor-pointer hover:fill-red-500 transition-colors' : ''}
              />
            )
          } else {
            const direction = toY > fromY ? 1 : -1
            return (
              <polygon
                key={`flow-${index}`}
                points={`${arrow.x},${arrow.y + size * direction} ${arrow.x - size/2},${arrow.y - size * direction} ${arrow.x + size/2},${arrow.y - size * direction}`}
                fill={connection.color}
                opacity={isDeleteMode ? "1" : "0.7"}
                className={isDeleteMode ? 'cursor-pointer hover:fill-red-500 transition-colors' : ''}
              />
            )
          }
        })}
        
        {/* Arrow head at the end */}
        {connection.type === 'arrow' && (
          <polygon
            points={`${toX},${toY} ${toX - arrowLength},${toY - arrowWidth} ${toX - arrowLength},${toY + arrowWidth}`}
            fill={connection.color}
            opacity={systemDesignData.selectedTool === 'delete' ? "1" : "1"}
            className={systemDesignData.selectedTool === 'delete' ? 'cursor-pointer hover:fill-red-500 transition-colors' : ''}
            style={{ 
              filter: systemDesignData.selectedTool === 'delete' ? 'drop-shadow(0 0 2px rgba(0,0,0,0.3))' : 'none'
            }}
          />
        )}
      </g>
    )
  }

  const exportDesign = () => {
    const designData = {
      components: systemDesignData.placedComponents,
      connections: systemDesignData.connections,
      textContent: systemDesignData.content,
      timestamp: new Date().toISOString()
    }
    const blob = new Blob([JSON.stringify(designData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'system-design.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  const getSaveStatus = () => {
    if (isDirty) {
      return <span className="text-amber-600">● Unsaved changes</span>
    } else if (lastSaved) {
      return <span className="text-green-600">✓ Saved {new Date(lastSaved).toLocaleTimeString()}</span>
    }
    return <span className="text-gray-500">No changes</span>
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 p-4 space-y-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Layers className="w-6 h-6 text-purple-600" />
            <h2 className="text-lg font-semibold">System Design</h2>
            
            {/* Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => store.setSystemDesignMode('visual')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  systemDesignData.designMode === 'visual' 
                    ? 'bg-white text-purple-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Visual Board
              </button>
              <button
                onClick={() => store.setSystemDesignMode('text')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  systemDesignData.designMode === 'text' 
                    ? 'bg-white text-purple-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Text Description
              </button>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <TimerDisplay
              formattedTime={formattedTime}
              timeColor={timeColor}
              timeStatus={timeStatus}
              isRunning={isRunning}
              isTimeUp={isTimeUp}
              onStart={startTimer}
              onPause={pauseTimer}
              onReset={resetTimer}
              showControls={true}
            />
            
            <div className="text-sm">
              {getSaveStatus()}
            </div>
            
            {systemDesignData.designMode === 'visual' && (
              <div className="flex space-x-2">
                <button
                  onClick={clearCanvas}
                  className="flex items-center space-x-1 px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Clear</span>
                </button>
                <button
                  onClick={exportDesign}
                  className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Export</span>
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="text-sm text-gray-600">
          Current challenge: <span className="font-semibold text-gray-800">{promptTitle}</span>.{' '}
          {systemDesignData.designMode === 'visual'
            ? 'Drag components onto the board to create your design.'
            : 'Describe your system components and architecture.'}
        </div>

        <QuestionPanel
          title={promptTitle}
          description={promptDescription}
          requirements={promptRequirements}
          timeLimit="60 minutes"
          isCollapsed={isQuestionCollapsed}
          onToggle={() => setIsQuestionCollapsed(!isQuestionCollapsed)}
        />
      </div>

      {/* Content */}
      <div className="flex-1 flex">
        {/* Left Panel - Components Library or Problem Statement */}
        <div className="w-1/4 p-4 border-r border-gray-200 overflow-y-auto max-h-screen">
          {systemDesignData.designMode === 'visual' ? (
            <div className="space-y-4 h-full flex flex-col">
              <div className="flex-shrink-0">
                <h3 className="font-semibold mb-3 text-gray-900">Prompt Overview</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-gray-700 space-y-2">
                  <p className="font-semibold text-blue-900 leading-tight">{promptTitle}</p>
                  <p className="leading-relaxed">{promptDescriptionPreview}</p>
                  {promptRequirementsPreview.length > 0 && (
                    <ul className="list-disc list-inside space-y-1">
                      {promptRequirementsPreview.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="flex-shrink-0">
                <h3 className="font-semibold mb-3 text-gray-900">System Components</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {SYSTEM_COMPONENTS.map((component) => {
                    const Icon = component.icon
                    return (
                      <div
                        key={component.id}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('componentId', component.id)}
                        className={`flex items-center space-x-3 p-3 ${component.color} text-white rounded-lg cursor-move hover:opacity-90 transition-opacity`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-sm font-medium">{component.name}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
              
              <div className="flex-shrink-0">
                <h3 className="font-semibold mb-3 text-gray-900">🔧 Design Tools</h3>
                <div className="space-y-2">
                  {DESIGN_TOOLS.map((tool) => {
                    const Icon = tool.icon
                    return (
                      <button
                        key={tool.id}
                        onClick={() => {
                          store.setSystemDesignTool(tool.id as any)
                          if (tool.id !== 'connect') setConnectingFrom(null)
                        }}
                        className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                          systemDesignData.selectedTool === tool.id
                            ? 'bg-purple-100 text-purple-700 border-2 border-purple-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-sm font-medium">{tool.name}</span>
                      </button>
                    )
                  })}
                </div>
                {connectingFrom && (
                  <div className="mt-2 p-2 bg-blue-50 text-blue-700 text-xs rounded border border-blue-200">
                    Click another component to connect
                  </div>
                )}
              </div>
              
              <div className="flex-shrink-0">
                <h4 className="font-semibold mb-2 text-gray-900">Instructions</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Drag components onto the board</li>
                  <li>• Use Move tool to reposition components</li>
                  <li>• Use Connect tool to draw arrows between components</li>
                  <li>• Use Delete tool to remove components/connections</li>
                  <li>• Consider load balancing & scaling</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2 text-gray-900">Problem Statement</h3>
                <div className="bg-blue-50 rounded-lg p-4 text-sm">
                  <p className="mb-3">
                    <strong>{promptTitle}</strong>
                  </p>
                  <p className="text-gray-700 whitespace-pre-line">{promptDescription}</p>
                </div>
              </div>

              {promptRequirements.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900">Requirements</h4>
                  <div className="space-y-2 text-sm">
                    {promptRequirements.map((requirement, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                        <span>{requirement}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {promptConsiderations.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900">Consider</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {promptConsiderations.map((consideration, index) => (
                      <li key={index}>• {consideration}</li>
                    ))}
                  </ul>
                </div>
              )}

              {promptAdditionalNotes && (
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900">Additional Notes</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{promptAdditionalNotes}</p>
                </div>
              )}

              {interviewerDetails && (interviewerDetails.firstName || interviewerDetails.lastName || interviewerDetails.title || interviewerDetails.email) && (
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900">Interviewer Guidance</h4>
                  <p className="text-sm text-gray-700">
                    {[interviewerDetails.firstName, interviewerDetails.lastName].filter(Boolean).join(' ') || 'Assigned Interviewer'}
                    {interviewerDetails.title ? ` • ${interviewerDetails.title}` : ''}
                  </p>
                  {interviewerDetails.email && (
                    <p className="text-xs text-gray-500">Contact: {interviewerDetails.email}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 p-4">
          {systemDesignData.designMode === 'visual' ? (
            <div className="h-full">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Design Board</h3>
              <div
                ref={canvasRef}
                onDrop={handleCanvasDrop}
                onDragOver={(e) => e.preventDefault()}
                className="w-full h-full bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg relative overflow-hidden"
                style={{ minHeight: '600px' }}
              >
                {systemDesignData.placedComponents.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-gray-400">
                      <Layers className="w-12 h-12 mx-auto mb-2" />
                      <p className="text-sm">Drag system components here to start designing</p>
                    </div>
                  </div>
                )}
                
                {/* SVG overlay for connections */}
                <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 1, pointerEvents: systemDesignData.selectedTool === 'delete' ? 'auto' : 'none' }}>
                  {systemDesignData.connections.map(renderConnection)}
                </svg>
                
                {systemDesignData.placedComponents.map((component) => {
                  const Icon = SYSTEM_COMPONENTS.find(c => c.id === component.componentId)?.icon || Database
                  return (
                    <div
                      key={component.id}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleComponentClick(component.id)
                      }}
                      className={`absolute ${component.color} text-white rounded-lg p-3 shadow-lg transition-all z-10 ${
                        systemDesignData.selectedTool === 'move' 
                          ? 'cursor-move hover:shadow-xl' 
                          : systemDesignData.selectedTool === 'connect'
                          ? `cursor-pointer hover:ring-2 hover:ring-blue-400 ${connectingFrom === component.id ? 'ring-2 ring-blue-400' : ''}`
                          : systemDesignData.selectedTool === 'delete'
                          ? 'cursor-pointer hover:bg-red-500'
                          : 'cursor-default'
                      }`}
                      style={{
                        left: component.x,
                        top: component.y,
                        width: component.width,
                        height: component.height
                      }}
                      onMouseDown={(e) => {
                        if (systemDesignData.selectedTool !== 'move') return
                        e.stopPropagation()
                        
                        // Simple drag implementation
                        const startX = e.clientX - component.x
                        const startY = e.clientY - component.y
                        
                        const handleMouseMove = (e: MouseEvent) => {
                          store.updateSystemDesignComponent(component.id, {
                            x: e.clientX - startX,
                            y: e.clientY - startY
                          })
                        }
                        
                        const handleMouseUp = () => {
                          document.removeEventListener('mousemove', handleMouseMove)
                          document.removeEventListener('mouseup', handleMouseUp)
                        }
                        
                        document.addEventListener('mousemove', handleMouseMove)
                        document.addEventListener('mouseup', handleMouseUp)
                      }}
                    >
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <Icon className="w-6 h-6 mb-1" />
                        <span className="text-xs font-medium">{component.name}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            <div className="h-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your System Design
              </label>
              <textarea
                value={systemDesignData.content || ''}
                onChange={(e) => handleContentChange(e.target.value)}
                placeholder="Describe your system architecture here...

Consider including:
- High-level architecture diagram
- Database schema design
- API endpoints
- Scalability strategies
- Technology choices and justifications

Example:
1. Load Balancer (NGINX)
   ↓
2. Web Servers (Node.js/Express)
   ↓
3. Application Logic
   ↓
4. Database Layer (PostgreSQL + Redis Cache)
   ↓
5. Message Queue (RabbitMQ)

User Service:
- Handles user registration, authentication
- Uses PostgreSQL for user data
- Redis for session management

Tweet Service:
- Handles tweet creation, retrieval
- Partitioned database by user_id
- Fan-out on write for timeline generation

Timeline Service:
- Pre-computed timelines in Redis
- Real-time updates via WebSocket
- Pagination for large timelines"
                className="w-full h-full p-3 border border-gray-300 rounded-md font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="system-design-editor"
              />
              <div className="mt-2 text-xs text-gray-500">
                Auto-saves every 5 seconds after you stop typing
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
